import { useEffect, useState, useCallback, useMemo, createContext, useContext, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";
import { errorLogger } from "@/lib/errorLogger";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const clearStaleLocalSession = async () => {
      try {
        // Recover from stale/invalid refresh tokens stored in local storage.
        // This clears local auth state without requiring a valid network refresh.
        await supabase.auth.signOut({ scope: "local" });
      } catch (error) {
        console.warn("Failed to clear local auth session:", error);
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        // Keep error logger and Sentry aware of current user for error attribution
        errorLogger.setUser(
          session?.user?.id ?? null,
          session?.user?.user_metadata?.full_name ?? null,
          session?.user?.email ?? null,
        );
        if (session?.user) {
          Sentry.setUser({
            id: session.user.id,
            email: session.user.email ?? undefined,
            username: session.user.user_metadata?.full_name ?? undefined,
          });
        } else {
          Sentry.setUser(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        const message = error.message.toLowerCase();
        if (message.includes("invalid refresh token") || message.includes("refresh token not found")) {
          await clearStaleLocalSession();
        } else {
          console.warn("Failed to restore auth session:", error);
        }
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Handle deep link for native app OAuth callback
    // (The hosted /auth/callback page will redirect into the app via this scheme.)
    let appUrlOpenHandle: { remove: () => Promise<void> | void } | null = null;

    if (Capacitor.isNativePlatform()) {
      (async () => {
        appUrlOpenHandle = await App.addListener("appUrlOpen", ({ url }) => {
          // IMPORTANT: keep this handler lean; do async work in a detached task.
          void (async () => {
            try {
              await Browser.close();
            } catch {
              // ignore
            }

            try {
              const urlObj = new URL(
                url.startsWith("qualflowsuite://") ? url : url.replace("qualflowsuite://", "https://placeholder.com/")
              );

              const errorDescription = urlObj.searchParams.get("error_description");
              if (errorDescription) {
                // Surfaced by the hosted callback page when OAuth fails.
                console.error("OAuth sign-in error:", decodeURIComponent(errorDescription));
                return;
              }

              const code = urlObj.searchParams.get("code");
              if (code) {
                await supabase.auth.exchangeCodeForSession(code);
                return;
              }

              // Handle token in URL fragment (implicit flow) - kept for backwards compatibility
              if (url.includes("access_token") || url.includes("refresh_token")) {
                const hashParams = new URLSearchParams(urlObj.hash.substring(1));
                const accessToken = hashParams.get("access_token");
                const refreshToken = hashParams.get("refresh_token");

                if (accessToken && refreshToken) {
                  await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken,
                  });
                }
              }
            } catch (e) {
              console.error("Failed to handle appUrlOpen:", e);
            }
          })();
        });
      })();
    }

    return () => {
      subscription.unsubscribe();
      if (appUrlOpenHandle) {
        void appUrlOpenHandle.remove();
      }
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
      // For native apps we redirect to the hosted callback page, which then deep-links back into the app.
      const redirectTo = "https://staff.superiorformulations.com.au/auth/callback?native=1";

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo,
          skipBrowserRedirect: true,
        },
      });

      if (error) throw error;

      if (data?.url) {
        await Browser.open({
          url: data.url,
          presentationStyle: "popover",
        });
      }

      return;
    }

    // Web browser flow
    const redirectTo = `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
      },
    });

    if (error) throw error;

    // Let the auth client handle the redirect.
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
  }), [user, session, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
